/**
 * Method group extracted from the Konva map Stimulus controller.
 * These functions intentionally use `this` so the thin Stimulus adapter can
 * keep the existing target/value/action API while the implementation lives in
 * smaller feature-oriented modules.
 */
import Konva from 'konva';
import {
  DRAW_WIDTH,
  ERASE_RADIUS,
  MAX_FOG_BRUSH_SIZE,
  MIN_FOG_BRUSH_SIZE,
  MAX_SCALE,
  MIN_SCALE,
  MIN_SHAPE_DIMENSION,
  POSITION_ZOOM_STEP,
  SHAPE_COLORS,
  SHAPE_DEFAULTS,
  SHAPE_TRANSFORMER_OPTIONS,
  TOOLBAR_DEFAULT_TOOLS,
  TOOLBAR_TOOLS,
  TOOLBAR_URL_PARAM,
  ZOOM_STEP
} from './constants.js';

function updateShapeColors(map, event, properties) {
  const shape = map._findShapeById(event.currentTarget.dataset.shapeId);
  const color = event.currentTarget.value;
  if (!shape || !/^#[0-9a-f]{6}$/i.test(color)) return;

  if (
    properties.includes('fillColor') &&
    !properties.includes('color') &&
    (!shape.fillColor || shape.fillColor === 'transparent')
  )
    shape.fillOpacity = 0;
  for (const property of properties) shape[property] = color;
  if (properties.includes('color')) {
    shape.konvaShape.stroke(color);
    map._recreateLabels(shape);
    map._updateShapesList();
  }
  if (properties.includes('fillColor')) shape.konvaShape.fill(map._computeFill(shape));
  map._shapesLayerAbove.batchDraw();
  map._shapesLayerBelow.batchDraw();
  map._refreshEditModal();
  map._save();
}

export const ActionsMethods = {
  setTool(event) {
    event.preventDefault();
    const tool = event.currentTarget.dataset.tool;
    if (tool) {
      this._brushDismissed = false;
      this._cancelCanvasGesture?.();
      this._applyTool(tool);
      this._rememberToolSelection(tool);
      if (this._mobileEditor) this.collapseMapControls();
    }
  },

  fillFog(event) {
    if (this.playerValue) return;
    if (event) event.preventDefault();
    const entry = this._fogLayer;
    if (!entry) return;
    if (!confirm('Cover the entire map with fog of war? This will hide the complete map from your players.')) return;
    this._fogPresentation?.beforeUpdate();

    entry.strokes.forEach((s) => s.destroy());
    entry.strokes = [];

    if (!entry.filled) {
      entry.baseRect = new Konva.Rect(this._fogRectAttrs(this._fogCoverageAttrs()));
      entry.group.add(entry.baseRect);
      entry.baseRect.moveToBottom();
      entry.filled = true;
    }

    entry.konvaLayer.batchDraw();
    this._fogPresentation?.afterUpdate();
    this._save();
  },

  clearFog(event) {
    if (this.playerValue) return;
    if (event) event.preventDefault();
    if (!confirm('Are you sure? This will reveal the complete map to your players.')) return;
    const entry = this._fogLayer;
    if (!entry) return;

    this._fogPresentation?.beforeUpdate();
    entry.strokes.forEach((s) => s.destroy());
    entry.strokes = [];

    if (entry.baseRect) {
      entry.baseRect.destroy();
      entry.baseRect = null;
      entry.filled = false;
    }

    entry.konvaLayer.batchDraw();
    this._fogPresentation?.afterUpdate();
    this._save();
  },

  zoomIn(event) {
    if (event) event.preventDefault();
    this._applyZoom(this._currentScale * ZOOM_STEP);
  },

  zoomOut(event) {
    if (event) event.preventDefault();
    this._applyZoom(this._currentScale / ZOOM_STEP);
  },

  setZoom(event) {
    event.preventDefault();
    const zoom = parseFloat(event.currentTarget.dataset.zoom);
    if (this.workspaceValue && this._positionManagerActive) {
      this._monitorZoom = zoom;
      this._applyPositionWindowDimensions();
      this._applyPositionManagerTransform();
      return;
    }
    if (!isNaN(zoom)) this._applyZoom(zoom);
  },

  resetView(event) {
    if (event) event.preventDefault();
    this._resetView();
  },

  updateMapNameOverlay(event) {
    if (!this.hasMapNameOverlayTarget) return;

    const name = event.currentTarget.value.trim();
    this.mapNameOverlayTarget.textContent = name || 'Untitled map';
  },

  toggleDrag(event) {
    if (event) event.preventDefault();
    if (this._positionManagerActive) return;
    this._applyTool(this._tool === 'drag' ? 'draw' : 'drag');
  },

  toggleSelect(event) {
    if (event) event.preventDefault();
    if (this._positionManagerActive) return;
    if (this._tool === 'select') {
      this._applyTool('draw');
      return;
    }

    this._showToolbar('shapes', 'select');
    this._applyTool('select');
  },

  toggleToolbar(event) {
    if (event) event.preventDefault();
    const toolbar = event.currentTarget.dataset.konvaMapToolbarButton;
    if (!toolbar) return;

    const element = this._toolbarElement(toolbar);
    if (element?.classList.contains('show')) {
      this._hideToolbar(toolbar);
    } else {
      this._showToolbar(toolbar);
    }
  },

  togglePositionManager(event) {
    if (event) event.preventDefault();
    if (this._tool === 'position-manager') {
      this._hideToolbar('position');
      return;
    }

    this._showToolbar('position', 'position-manager');
  },

  changeCharacterPlacement(event) {
    this.characterPlacementValue = event.currentTarget.value;
    this.effectiveCharacterPlacementValue = this._effectiveCharacterPlacement();
    this.syncRadioButtonLabels(event);
  },

  changeGameViewRotation(event) {
    this.gameViewRotationValue = event.currentTarget.value;
    this.effectiveCharacterPlacementValue = this._effectiveCharacterPlacement();
    this.syncRadioButtonLabels(event);
  },

  syncRadioButtonLabels(event) {
    const input = event.currentTarget;
    const group = input.closest('.btn-group');
    if (!group) return;

    group.querySelectorAll('.btn-check').forEach((radio) => {
      const label = group.querySelector(`label[for="${radio.id}"]`);
      if (!label) return;

      label.classList.toggle('btn-secondary', radio.checked);
      label.classList.toggle('btn-outline-secondary', !radio.checked);
    });
  },

  _effectiveCharacterPlacement() {
    const rotationDegrees = { zero: 0, ninety: 90, one_eighty: 180, two_seventy: 270 };
    const placementDegrees = { top: 0, right: 90, bottom: 180, left: 270 };
    const placementByDegrees = { 0: 'top', 90: 'right', 180: 'bottom', 270: 'left' };
    const degrees =
      (rotationDegrees[this.gameViewRotationValue] - placementDegrees[this.characterPlacementValue] + 360) % 360;

    return placementByDegrees[degrees] || 'top';
  },

  onPositionZoomInputChange(event) {
    this.positionZoomValue = Math.max(-95, Math.min(900, parseFloat(event.currentTarget.value) || 0));
  },

  onPositionPosXInputChange(event) {
    this.positionPosXValue = parseFloat(event.currentTarget.value) || 0;
  },

  onPositionPosYInputChange(event) {
    this.positionPosYValue = parseFloat(event.currentTarget.value) || 0;
  },

  resetPosition(event) {
    if (event) event.preventDefault();
    this.positionZoomValue = 0;
    this.positionPosXValue = 0;
    this.positionPosYValue = 0;
    this._savePosition();
  },

  savePosition(event) {
    if (event) event.preventDefault();
    this.positionZoomValue = Math.round(this.positionZoomValue);
    this.positionPosXValue = Math.round(this.positionPosXValue);
    this.positionPosYValue = Math.round(this.positionPosYValue);
    this._savePosition();
  },

  scrollFogBrushSize(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!event.deltaY) return;
    const input = this.fogBrushSizeInputTarget;
    input.value = this._clampFogBrushSize(this._currentFogBrushSize() + Math.sign(event.deltaY) * 3);
    this.changeFogBrushSize({ currentTarget: input });
  },

  changeFogBrushSize(event) {
    const size = this._clampFogBrushSize(parseInt(event.currentTarget.value, 10));
    if (this._tool === 'erase') {
      this.eraseBrushSizeValue = size;
    } else {
      this.drawBrushSizeValue = size;
    }
  },

  deleteShape(event) {
    event.preventDefault();
    event.stopPropagation?.();
    const shape = this._findShapeById(event.currentTarget.dataset.shapeId);
    if (shape && confirm(`Delete ${shape.name}?`)) this._deleteShapeById(shape.id);
  },

  editShape(event) {
    event.preventDefault();
    const closeDialog = this.workspaceValue && this._mobileEditor && event.currentTarget.closest('dialog');
    this._showToolbar('shapes', 'select');
    this._selectShape(event.currentTarget.dataset.shapeId);
    if (closeDialog) window.modal?.hideModal();
    if (event.detail === 0) this._focusShapeControls(event.currentTarget.dataset.shapeId);
  },

  toggleShapeAboveFog(event) {
    if (this.playerValue) return;
    event.preventDefault();
    const shape = this._findShapeById(event.currentTarget.dataset.shapeId);
    if (!shape) return;
    shape.aboveFog = !shape.aboveFog;
    this._moveShapeToCorrectLayer(shape);
    this._updateShapesList();
    this._refreshEditModal();
    this._save();
  },

  changeShapeColor(event) {
    updateShapeColors(this, event, ['color', 'fillColor']);
  },

  changeLayerColor(event) {
    updateShapeColors(this, event, ['color']);
  },

  renameShape(event) {
    const shape = this._findShapeById(event.currentTarget.dataset.shapeId);
    if (!shape) return;
    if (shape.type === 'character') return;
    shape.name = event.currentTarget.value;
    this._recreateLabels(shape);
    this._getShapeLayer(shape).batchDraw();
    this._updateShapesList();
    this._refreshEditModalTitle();
    this._save();
  },

  focusShape(event) {
    const shape = this._findShapeById(event.currentTarget.dataset.shapeId);
    if (shape?.konvaShape) {
      shape.konvaShape.dash([8, 4]);
      this._shapesLayerAbove.batchDraw();
    }
  },

  blurShape(event) {
    const shape = this._findShapeById(event.currentTarget.dataset.shapeId);
    if (shape?.konvaShape) {
      if (this._selectedShapeId !== shape.id) shape.konvaShape.dash([]);
      this._shapesLayerAbove.batchDraw();
    }
  },

  toggleShapeLabels(event) {
    event.preventDefault();
    const shape = this._findShapeById(event.currentTarget.dataset.shapeId);
    if (!shape) return;
    shape.showLabels = !shape.showLabels;
    if (shape.konvaLabels) {
      shape.konvaLabels.visible(shape.showLabels);
      this._getShapeLayer(shape).batchDraw();
    }
    this._updateShapesList();
    this._refreshEditModal();
    this._save();
  },

  changeStrokeWidth(event) {
    const shape = this._findShapeById(event.currentTarget.dataset.shapeId);
    if (!shape) return;
    shape.strokeWidth = parseInt(event.currentTarget.value) || SHAPE_DEFAULTS.strokeWidth;
    shape.konvaShape.strokeWidth(shape.strokeWidth);
    this._getShapeLayer(shape).batchDraw();
    this._refreshEditModal();
    this._save();
  },

  changeFillColor(event) {
    updateShapeColors(this, event, ['fillColor']);
  },

  changeFillOpacity(event) {
    const shape = this._findShapeById(event.currentTarget.dataset.shapeId);
    const rawValue = event.currentTarget.value;
    const percentage = Number(rawValue);
    if (
      !shape ||
      shape.type === 'character' ||
      rawValue === '' ||
      !Number.isFinite(percentage) ||
      percentage < 0 ||
      percentage > 100
    )
      return;
    shape.fillOpacity = percentage / 100;
    if (percentage > 0 && (!shape.fillColor || shape.fillColor === 'transparent')) shape.fillColor = shape.color;
    shape.konvaShape.fill(this._computeFill(shape));
    this._shapesLayerAbove.batchDraw();
    this._shapesLayerBelow.batchDraw();
    this._refreshEditModal();
    this._save();
  }
};
